import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import useResponsiveDialog from '@/hooks/utils/useResponsiveDialog';
import { useUserActivity } from '@/hooks/useUserActivity';
import Image from 'next/image';
import { InfoPrimaryIcon } from '@/assets/icons';
import { Button } from '@/components/common/Button';
import cn from '@/utils/tailwind';
import { ONE_SECOND_MS } from '@fintronners/react-utils/src/envConstants';
import {
  PATHS,
  TIME_UNTIL_INACTIVITY_ALERT_MS,
  TIME_UNTIL_REDIRECTION_MS,
} from '@/utils/constants';

const logOut = async () => {
  const response = await fetch('/api/logout', {
    method: 'POST',
  });

  if (!response.ok) {
    console.error('Failed to logout the user.');
  }
};

const InactivityDialog = () => {
  const router = useRouter();
  const { t } = useTranslation();
  const { ResponsiveDialog } = useResponsiveDialog();

  const [showInactiveConfirmation, setShowInactiveConfirmation] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  const handleInactive = useCallback(() => {
    setShowInactiveConfirmation(true);
  }, []);

  const { resetActivity } = useUserActivity(handleInactive, TIME_UNTIL_INACTIVITY_ALERT_MS);

  useEffect(() => {
    if (!showInactiveConfirmation) {
      return;
    }

    const redirectTimer = window.setTimeout(async () => {
      await logOut();

      router.push(PATHS.SESSION_EXPIRED);
    }, TIME_UNTIL_REDIRECTION_MS);

    return () => {
      clearTimeout(redirectTimer);
    };
  }, [showInactiveConfirmation]);

  useEffect(() => {
    if (!showInactiveConfirmation) {
      return;
    }

    // We're using this state not only to display the remaining time to the user,
    // but also to disable the button while the logout is happening
    // This will prevent the edge case of users clicking to stay in the page after
    // the logout request being initiated, what would invalidate their tokens
    setRemainingSeconds(TIME_UNTIL_REDIRECTION_MS / ONE_SECOND_MS);

    const secondsTimer = window.setInterval(() => {
      setRemainingSeconds((seconds) => Math.max(0, --seconds));
    }, ONE_SECOND_MS);

    return () => {
      clearTimeout(secondsTimer);
    };
  }, [showInactiveConfirmation]);

  const confirmAction = () => {
    resetActivity();
    setShowInactiveConfirmation(false);
  };

  return (
    <ResponsiveDialog.Root
      open={showInactiveConfirmation}
      onOpenChange={(isOpen) => setShowInactiveConfirmation(isOpen)}
      dismissible={false}
    >
      <ResponsiveDialog.Content hideCloseButton>
        <ResponsiveDialog.Header>
          <Image alt="Info icon" width={50} height={50} src={InfoPrimaryIcon.src} />
          <ResponsiveDialog.Title>{t('inactivityAlert.title')}</ResponsiveDialog.Title>
        </ResponsiveDialog.Header>

        <div className={cn('whitespace-pre-line text-grey72', 'text-center')}>
          {t('inactivityAlert.ctaDescription', {
            remainingSeconds,
            // TODO: Improve this by grabbing it from config
            targetName: 'Alight Worklife Experience',
          })}
        </div>

        <ResponsiveDialog.Footer>
          <div className="flex w-full flex-col space-y-5">
            <Button
              className="w-full"
              variant="primary"
              onClick={confirmAction}
              disabled={remainingSeconds <= 0}
            >
              {t('inactivityAlert.ctaButton')}
            </Button>
          </div>
        </ResponsiveDialog.Footer>
      </ResponsiveDialog.Content>
    </ResponsiveDialog.Root>
  );
};

export default InactivityDialog;
